import type { Country } from "@/i18n/config"
import { blobMediaUrl } from "@/lib/blob-url"
import { type GateId, isAllowedInCountry } from "@/lib/country-gates"

/**
 * Blood test add-ons shown on `/pricing`, `/go/bloodtest` and the
 * advanced-blood-test detail page. Single source of truth for the add-on set
 * (titles, regular fallback price, image, /tests link).
 *
 * - No add-on carries a price here — display resolves live Stripe first, then
 *   the central fallback table in lib/pricing.ts (`FALLBACK_PRICES_CHF`),
 *   which is the single place a CHF figure is written down.
 * - Member prices are NOT here — they come from Stripe (`get-products`
 *   `member_price`), piggybacked into the price maps and read via the helpers
 *   in `@/lib/pricing/member-prices`, so the quiz and the add-on cards read one
 *   source and can't drift.
 */
export const BLOOD_ADDONS = [
  // Ordered as the booking flow lists them. In CH the B-vitamins add-on is the
  // B-vitamins & homocysteine test (AHD-12960): same price key, same page, CH
  // copy in the CH message files.
  {
    titleKey: "addonBVitamins" as const,
    descKey: "addonBVitaminsDesc" as const,
    imageSrc: blobMediaUrl("/images/addons/multi-vial.png"),
    slug: "essential-b-vitamins-test",
  },
  // Single-marker add-ons (Lp(a) / PSA / Cortisol). They ARE in the Stripe
  // price map (`PRODUCT_PRICE_KEYS` / `PRICE_KEY_FOR_SLUG`), so they read the
  // live figure like every other add-on. Their detail pages are literal routes
  // under (default-nav)/tests behind a `page:` gate, so the card only links
  // where that gate lets the page exist (see `bloodAddonPath`).
  {
    titleKey: "addonLpa" as const,
    descKey: "addonLpaDesc" as const,
    imageSrc: blobMediaUrl("/images/addons/single-vial.png"),
    slug: "lpa",
    gate: "page:tests/lpa" as GateId,
  },
  {
    titleKey: "addonHormoneMen" as const,
    descKey: "addonHormoneMenDesc" as const,
    imageSrc: blobMediaUrl("/images/addons/multi-vial-purple.png"),
    slug: "hormone-blood-test-men",
  },
  {
    titleKey: "addonHormoneWomen" as const,
    descKey: "addonHormoneWomenDesc" as const,
    imageSrc: blobMediaUrl("/images/addons/multi-vial-purple.png"),
    slug: "hormone-blood-test-women",
  },
  {
    titleKey: "addonOmega" as const,
    descKey: "addonOmegaDesc" as const,
    imageSrc: blobMediaUrl("/images/addons/multi-vial.png"),
    slug: "omega-blood-test",
  },
  {
    titleKey: "addonCortisol" as const,
    descKey: "addonCortisolDesc" as const,
    imageSrc: blobMediaUrl("/images/addons/single-vial.png"),
    slug: "cortisol-blood-test",
    gate: "page:tests/cortisol-blood-test" as GateId,
  },
  {
    titleKey: "addonPsa" as const,
    descKey: "addonPsaDesc" as const,
    imageSrc: blobMediaUrl("/images/addons/single-vial.png"),
    slug: "psa-blood-test",
    gate: "page:tests/psa-blood-test" as GateId,
  },
  // CH-only add-ons (P-AHD-333); their pages 404 elsewhere, and ADDON_INFO
  // hides the cards there too.
  {
    titleKey: "addonCoeliac" as const,
    descKey: "addonCoeliacDesc" as const,
    imageSrc: blobMediaUrl("/images/addons/single-vial.png"),
    slug: "coeliac-blood-test",
  },
  {
    titleKey: "addonHeavyMetals" as const,
    descKey: "addonHeavyMetalsDesc" as const,
    imageSrc: blobMediaUrl("/images/addons/multi-vial.png"),
    slug: "heavy-metals-blood-test",
  },
  {
    titleKey: "addonTraceElements" as const,
    descKey: "addonTraceElementsDesc" as const,
    imageSrc: blobMediaUrl("/images/addons/multi-vial.png"),
    slug: "trace-elements-blood-test",
  },
]

/** The add-on's `/tests` path in `country`, or undefined where its page is
 *  gated out (the card then renders unlinked). */
export function bloodAddonPath(
  addon: (typeof BLOOD_ADDONS)[number],
  country: string
): string | undefined {
  if (addon.gate && !isAllowedInCountry(addon.gate, country as Country))
    return undefined
  return `/tests/${addon.slug}`
}
